import React, { FC } from 'react';
import { createCrop } from 'utils/image.utils';
import Imgix from 'react-imgix';
import { styled } from '@mui/material';
import { ImageVariant, IMediaItem } from 'FeatureModules/Media/types/media.types';
import Icon, { TIconName, IIconProps } from 'DesignSystem/Primitives/Icon/Icon';

interface IImageProps {
  image?: ImageVariant | IMediaItem | null;
  /**
   * @deprecated use icon instead
   */
  backupImageUrl?: string;
  /**
   * Will be shown if image is not available
   */
  backupIcon?: TIconName;
  backupIconProps?: Omit<IIconProps, 'name'>;
  width?: number;
  height?: number;
  alt?: string;
  borderRadius?: number;
}

const ImageContainer = styled('div')<{
  width?: number | string;
  height?: number | string;
  backgroundImage?: string;
}>(({ width, height, backgroundImage }) => ({
  backgroundImage: `url(${backgroundImage})`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  width: width ? width : '100%',
  height: height ? height : '100%',
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const ImgixImage = styled(Imgix)<{ borderRadius: number }>(({ borderRadius }) => ({
  width: 'auto',
  height: 'auto',
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
  flexShrink: 0,
  borderRadius: borderRadius,
}));

const Image: FC<IImageProps> = ({
  image,
  width,
  height,
  backupImageUrl,
  backupIcon,
  backupIconProps = { color: 'gray-80', size: 'medium' },
  alt,
  borderRadius = 4,
}) => {
  const cropString = image?.crop ? createCrop(image?.crop) : null;

  return (
    <ImageContainer
      width={width}
      height={height}
      backgroundImage={!image ? backupImageUrl : undefined}
    >
      {image && (
        <ImgixImage
          width={width}
          height={height}
          src={image.url || ''}
          borderRadius={borderRadius}
          htmlAttributes={{ loading: 'lazy', alt: alt || '' }}
          imgixParams={{
            ...(cropString?.length && {
              rect: cropString,
            }),
          }}
        />
      )}
      {!image && backupIcon && <Icon {...backupIconProps} name={backupIcon} />}
    </ImageContainer>
  );
};

export default Image;
